"use client";

import React from "react";
import {
    Box,
    LoadingOverlay,
    Text,
    Tabs,
    Stack,
    TextInput,
    Select,
    MultiSelect,
    Button,
    Group,
    ColorInput,
    ActionIcon,
    Loader,
    Center,
    useMantineColorScheme,
} from "@mantine/core";
import { Play, Plus, Trash, Palette, Database, Shield, Eye, Settings2, FileSpreadsheet, ArrowUpDown } from "lucide-react";
import { UseFormReturnType } from "@mantine/form";
import { BaseContainer, ConfigTabs, ConfigTabItem } from "../common";
import type {
    ChartInput,
    ChartConfig,
    Report,
} from "@bi-studio/shared/lib";
import {
    BarChartIcon,
    LineChartIcon,
    StackedLineChartIcon,
    BarLineChartIcon,
    PieChartIcon,
    GaugeChartIcon,
    RadarChartIcon,
    ScoreCardIcon,
    AreaChartIcon,
    CalendarChartIcon,
    MapChartIcon,
    CorrelationChartIcon,
    ScatterChartIcon,
} from "@bi-studio/shared/components";
import { ChartConditionalFormatting } from "./ChartConditionalFormatting";
import { RoleSelector } from "@/components/common/RoleSelector";
import { ChartExampleData } from "./ChartExampleData";

export interface ChartCreateViewProps {
    isSaving: boolean;
    activeTab: string | null;
    setActiveTab: (tab: string | null) => void;
    form: UseFormReturnType<ChartInput>;
    reports: Report[];
    columnNames: string[];
    isLoadingPreview: boolean;
    loadPreview: () => void;
    previewData: any[];
    chartContainerRef: React.RefObject<HTMLDivElement>;
    colorPresets: { name: string, colors: string[] }[];
    error?: string | null;
}

export function ChartCreateView({
    isSaving,
    activeTab,
    setActiveTab,
    form,
    reports,
    columnNames,
    isLoadingPreview,
    loadPreview,
    previewData,
    chartContainerRef,
    colorPresets,
}: ChartCreateViewProps) {
    const { colorScheme } = useMantineColorScheme();

    const tabs: ConfigTabItem[] = [
        { value: "general", label: "General", icon: <Settings2 size={14} /> },
        { value: "mapping", label: "Mapping", icon: <Database size={14} /> },
        { value: "formatting", label: "Formatting", icon: <Palette size={14} /> },
        { value: "permissions", label: "Permissions", icon: <Shield size={14} /> },
        { value: "preview", label: "Preview", icon: <Eye size={14} /> },
        { value: "example", label: "Sample", icon: <FileSpreadsheet size={14} /> },
    ];

    const updateConfig = (updates: Partial<ChartConfig>) => {
        form.setFieldValue("config", { ...form.values.config, ...updates });
    };

    const handleAddColor = () => {
        const currentColors = (form.values.config as any).colors as string[] || [];
        updateConfig({ colors: [...currentColors, "#212529"] });
    };

    const handleRemoveColor = (index: number) => {
        const currentColors = (form.values.config as any).colors as string[] || [];
        updateConfig({ colors: currentColors.filter((_, i) => i !== index) });
    };

    const handleUpdateColor = (index: number, color: string) => {
        const currentColors = [...((form.values.config as any).colors as string[] || [])];
        currentColors[index] = color;
        updateConfig({ colors: currentColors });
    };

    const config = form.values.config as any;

    return (
        <BaseContainer>
            <LoadingOverlay visible={isSaving} overlayProps={{ blur: 2 }} />

            <Box p={{ base: 'xs', sm: 'md' }}>
                <ConfigTabs
                    value={activeTab}
                    onChange={setActiveTab}
                    tabs={tabs}
                >
                    <Tabs.Panel value="general">
                        <Stack gap="md">
                            <Select
                                label="Report"
                                placeholder="Select a report"
                                data={reports.map((report) => ({
                                    value: report.id,
                                    label: report.name,
                                }))}
                                disabled={isSaving}
                                required
                                searchable
                                {...form.getInputProps("reportId")}
                            />

                            <Button
                                variant="light"
                                color="gray"
                                size="sm"
                                leftSection={<Play size={14} />}
                                onClick={loadPreview}
                                loading={isLoadingPreview}
                                disabled={!form.values.reportId}
                                fullWidth
                            >
                                Run Report to Map Columns
                            </Button>

                            <Select
                                label="Chart Type"
                                data={[
                                    { value: "bar", label: "Bar Chart" },
                                    { value: "line", label: "Line Chart" },
                                    { value: "stacked-line", label: "Stacked Line Chart" },
                                    { value: "stacked-bar-line", label: "Bar-Line Chart" },
                                    { value: "pie", label: "Pie Chart" },
                                    { value: "scorecard", label: "Score Card" },
                                    { value: "area", label: "Area Chart" },
                                    { value: "calendar", label: "Calendar Chart" },
                                    { value: "gauge", label: "Gauge Chart" },
                                    { value: "radar", label: "Radar Chart" },
                                    { value: "map", label: "Map Chart (Turkey)" },
                                    { value: "correlation", label: "Correlation Heatmap" },
                                    { value: "scatter", label: "Scatter Chart" },
                                ]}
                                disabled={isSaving}
                                required
                                leftSection={(() => {
                                    const type = form.values.type;
                                    const iconSize = 20;
                                    if (type === "bar") return <BarChartIcon size={iconSize} />;
                                    if (type === "line") return <LineChartIcon size={iconSize} />;
                                    if (type === "stacked-line") return <StackedLineChartIcon size={iconSize} />;
                                    if (type === "stacked-bar-line") return <BarLineChartIcon size={iconSize} />;
                                    if (type === "pie") return <PieChartIcon size={iconSize} />;
                                    if (type === "gauge") return <GaugeChartIcon size={iconSize} />;
                                    if (type === "radar") return <RadarChartIcon size={iconSize} />;
                                    if (type === "scorecard") return <ScoreCardIcon size={iconSize} />;
                                    if (type === "area") return <AreaChartIcon size={iconSize} />;
                                    if (type === "calendar") return <CalendarChartIcon size={iconSize} />;
                                    if (type === "map") return <MapChartIcon size={iconSize} />;
                                    if (type === "correlation") return <CorrelationChartIcon size={iconSize} />;
                                    if (type === "scatter") return <ScatterChartIcon size={iconSize} />;
                                    return <BarChartIcon size={iconSize} />;
                                })()}
                                renderOption={({ option }) => {
                                    const iconSize = 18;
                                    let Icon = BarChartIcon;
                                    if (option.value === "line") Icon = LineChartIcon;
                                    if (option.value === "stacked-line") Icon = StackedLineChartIcon;
                                    if (option.value === "stacked-bar-line") Icon = BarLineChartIcon;
                                    if (option.value === "pie") Icon = PieChartIcon;
                                    if (option.value === "gauge") Icon = GaugeChartIcon;
                                    if (option.value === "radar") Icon = RadarChartIcon;
                                    if (option.value === "scorecard") Icon = ScoreCardIcon;
                                    if (option.value === "area") Icon = AreaChartIcon;
                                    if (option.value === "calendar") Icon = CalendarChartIcon;
                                    if (option.value === "map") Icon = MapChartIcon;
                                    if (option.value === "correlation") Icon = CorrelationChartIcon;
                                    if (option.value === "scatter") Icon = ScatterChartIcon;
                                    
                                    return (
                                        <Group gap="sm">
                                            <Icon size={iconSize} />
                                            <Text size="sm">{option.label}</Text>
                                        </Group>
                                    );
                                }}
                                {...form.getInputProps("type")}
                            />

                            <TextInput
                                label="Chart Title"
                                placeholder="Sales Overview"
                                disabled={isSaving}
                                required
                                value={config.title || ""}
                                onChange={(e) => updateConfig({ title: e.target.value })}
                            />
                        </Stack>
                    </Tabs.Panel>

                    <Tabs.Panel value="mapping">
                        <Stack gap="md">
                            {columnNames.length === 0 && (
                                <Text size="sm" c="dimmed" ta="center" py="xl">
                                    Select a report in the "General" tab to configure mappings.
                                </Text>
                            )}

                            {(form.values.type === "bar" || form.values.type === "scatter") && (
                                <>
                                    <Select
                                        label="X Field"
                                        data={columnNames}
                                        placeholder="Select category column"
                                        disabled={isSaving || columnNames.length === 0}
                                        clearable
                                        searchable
                                        value={config.xField || ""}
                                        onChange={(value) => updateConfig({ xField: value ?? undefined })}
                                    />
                                    <Select
                                        label="Y Field"
                                        data={columnNames}
                                        placeholder="Select value column"
                                        disabled={isSaving || columnNames.length === 0}
                                        clearable
                                        searchable
                                        value={config.yField || ""}
                                        onChange={(value) => updateConfig({ yField: value ?? undefined })}
                                    />
                                </>
                            )}

                            {(form.values.type === "line" || form.values.type === "stacked-line") && (
                                <>
                                    <Select
                                        label="X Field"
                                        data={columnNames}
                                        placeholder="Select date/category column"
                                        disabled={isSaving || columnNames.length === 0}
                                        clearable
                                        searchable
                                        required
                                        value={config.xField || ""}
                                        onChange={(value) => updateConfig({ xField: value ?? undefined })}
                                    />
                                    {form.values.type === "stacked-line" ? (
                                        <MultiSelect
                                            label="Y Fields"
                                            data={columnNames}
                                            placeholder="Select value columns"
                                            disabled={isSaving || columnNames.length === 0}
                                            clearable
                                            searchable
                                            required
                                            value={config.yFields || []}
                                            onChange={(value) => updateConfig({ yFields: value })}
                                        />
                                    ) : (
                                        <Select
                                            label="Y Field"
                                            data={columnNames}
                                            placeholder="Select value column"
                                            disabled={isSaving || columnNames.length === 0}
                                            clearable
                                            searchable
                                            required
                                            value={config.yField || ""}
                                            onChange={(value) => updateConfig({ yField: value ?? undefined })}
                                        />
                                    )}
                                    <Select
                                        label="Series Field (optional)"
                                        data={columnNames}
                                        placeholder="Column for grouping lines"
                                        disabled={isSaving || columnNames.length === 0}
                                        clearable
                                        searchable
                                        value={config.seriesField || ""}
                                        onChange={(value) => updateConfig({ seriesField: value ?? undefined })}
                                    />
                                </>
                            )}

                            {form.values.type === "pie" && (
                                <>
                                    <Select
                                        label="Label Field"
                                        data={columnNames}
                                        placeholder="Select label column"
                                        disabled={isSaving || columnNames.length === 0}
                                        clearable
                                        searchable
                                        value={config.labelField || ""}
                                        onChange={(value) => updateConfig({ labelField: value ?? undefined })}
                                    />
                                    <Select
                                        label="Value Field"
                                        data={columnNames}
                                        placeholder="Select value column"
                                        disabled={isSaving || columnNames.length === 0}
                                        clearable
                                        searchable
                                        value={config.valueField || ""}
                                        onChange={(value) => updateConfig({ valueField: value ?? undefined })}
                                    />
                                </>
                            )}

                            {form.values.type === "scorecard" && (
                                <>
                                    <Select
                                        label="Value Field"
                                        data={columnNames}
                                        placeholder="Select value column"
                                        disabled={isSaving || columnNames.length === 0}
                                        clearable
                                        searchable
                                        required
                                        value={config.valueField || ""}
                                        onChange={(value) => updateConfig({ valueField: value ?? undefined })}
                                    />
                                    <TextInput
                                        label="Row Index (optional)"
                                        type="number"
                                        placeholder="0"
                                        disabled={isSaving}
                                        value={String(config.rowIndex ?? 0)}
                                        onChange={(e) => updateConfig({ rowIndex: parseInt(e.target.value) || 0 })}
                                    />
                                </>
                            )}

                            {form.values.type === "stacked-bar-line" && (
                                <>
                                    <Select
                                        label="X Field"
                                        data={columnNames}
                                        placeholder="Select category column"
                                        disabled={isSaving || columnNames.length === 0}
                                        clearable
                                        searchable
                                        required
                                        value={config.xField || ""}
                                        onChange={(value) => updateConfig({ xField: value ?? undefined })}
                                    />
                                    <Select
                                        label="Y Field (Bar)"
                                        data={columnNames}
                                        placeholder="Select bar value column"
                                        disabled={isSaving || columnNames.length === 0}
                                        clearable
                                        searchable
                                        required
                                        value={config.yFieldBar || ""}
                                        onChange={(value) => updateConfig({ yFieldBar: value ?? undefined })}
                                    />
                                    <Select
                                        label="Y Field (Line)"
                                        data={columnNames}
                                        placeholder="Select line value column"
                                        disabled={isSaving || columnNames.length === 0}
                                        clearable
                                        searchable
                                        required
                                        value={config.yFieldLine || ""}
                                        onChange={(value) => updateConfig({ yFieldLine: value ?? undefined })}
                                    />
                                    <Select
                                        label="Line Curve"
                                        data={[
                                            { value: "linear", label: "Linear" },
                                            { value: "basis", label: "Smooth" },
                                            { value: "step", label: "Step" },
                                        ]}
                                        disabled={isSaving}
                                        value={(config.curve as string) || "linear"}
                                        onChange={(val) => updateConfig({ curve: val } as any)}
                                    />
                                </>
                            )}

                            {form.values.type === "area" && (
                                <>
                                    <Select
                                        label="X Field"
                                        data={columnNames}
                                        placeholder="Select category/date column"
                                        disabled={isSaving || columnNames.length === 0}
                                        clearable
                                        searchable
                                        required
                                        value={config.xField || ""}
                                        onChange={(value) => updateConfig({ xField: value ?? undefined })}
                                    />
                                    <Select
                                        label="Group By (Series Field)"
                                        data={columnNames}
                                        placeholder="Optionally select a series to group by"
                                        disabled={isSaving || columnNames.length === 0}
                                        clearable
                                        searchable
                                        value={config.seriesField || ""}
                                        onChange={(value) => updateConfig({ seriesField: value ?? undefined })}
                                    />
                                    <Text size="xs" c="dimmed" mt="-xs">Grouping will create multiple areas based on unique values in this column.</Text>

                                    <Stack gap="xs">
                                        <Text size="sm" fw={500}>Values</Text>
                                        <MultiSelect
                                            label="Y Fields"
                                            data={columnNames}
                                            placeholder="Select one or more value columns"
                                            disabled={isSaving || columnNames.length === 0 || !!config.seriesField}
                                            clearable
                                            searchable
                                            value={config.yFields || []}
                                            onChange={(value) => updateConfig({ yFields: value })}
                                        />
                                        <Select
                                            label="Single Y Field (fallback)"
                                            data={columnNames}
                                            placeholder="Select value column"
                                            disabled={isSaving || columnNames.length === 0 || ((config.yFields as string[] || []).length > 0)}
                                            clearable
                                            searchable
                                            value={config.yField || ""}
                                            onChange={(value) => updateConfig({ yField: value ?? undefined })}
                                        />
                                    </Stack>

                                    <Group gap="xl">
                                        <Select
                                            label="Curve"
                                            data={[
                                                { value: "linear", label: "Linear" },
                                                { value: "basis", label: "Smooth" },
                                                { value: "step", label: "Step" },
                                            ]}
                                            disabled={isSaving}
                                            style={{ flex: 1 }}
                                            value={(config.curve as string) || "linear"}
                                            onChange={(val: any) => updateConfig({ curve: val })}
                                        />
                                        <Stack gap={0} style={{ flex: 1 }}>
                                            <Text size="sm" fw={500}>Stack Areas</Text>
                                            <Group grow>
                                                <Button
                                                    variant={config.stacked ? "filled" : "outline"}
                                                    size="xs"
                                                    onClick={() => updateConfig({ stacked: true })}
                                                >
                                                    Stacked
                                                </Button>
                                                <Button
                                                    variant={!config.stacked ? "filled" : "outline"}
                                                    size="xs"
                                                    onClick={() => updateConfig({ stacked: false })}
                                                >
                                                    Overlay
                                                </Button>
                                            </Group>
                                        </Stack>
                                    </Group>
                                </>
                            )}

                            {form.values.type === "calendar" && (
                                <>
                                    <Select
                                        label="Date Field"
                                        data={columnNames}
                                        placeholder="Select date column"
                                        disabled={isSaving || columnNames.length === 0}
                                        clearable
                                        searchable
                                        required
                                        value={config.dateField || ""}
                                        onChange={(value) => updateConfig({ dateField: value ?? undefined })}
                                    />
                                    <Select
                                        label="Value Field"
                                        data={columnNames}
                                        placeholder="Select numeric value column"
                                        disabled={isSaving || columnNames.length === 0}
                                        clearable
                                        searchable
                                        required
                                        value={config.valueField || ""}
                                        onChange={(value) => updateConfig({ valueField: value ?? undefined })}
                                    />
                                    <Group grow>
                                        <TextInput
                                            label="Year"
                                            placeholder="e.g. 2024"
                                            disabled={isSaving}
                                            value={String(config.year || "")}
                                            onChange={(e) => updateConfig({ year: e.target.value })}
                                        />
                                        <TextInput
                                            label="Min Value"
                                            placeholder="Automatic"
                                            type="number"
                                            disabled={isSaving}
                                            value={String(config.minValue ?? "")}
                                            onChange={(e) => updateConfig({ minValue: e.target.value ? Number(e.target.value) : undefined })}
                                        />
                                        <TextInput
                                            label="Max Value"
                                            placeholder="Automatic"
                                            type="number"
                                            disabled={isSaving}
                                            value={String(config.maxValue ?? "")}
                                            onChange={(e) => updateConfig({ maxValue: e.target.value ? Number(e.target.value) : undefined })}
                                        />
                                    </Group>
                                    <Text size="xs" c="dimmed" mt="-xs">
                                        Year is optional and will be inferred from data if not provided. Min/Max values control the color gradient.
                                    </Text>
                                </>
                            )}

                            {form.values.type === "gauge" && (
                                <>
                                    <Select
                                        label="Value Field"
                                        data={columnNames}
                                        placeholder="Select numeric value column"
                                        disabled={isSaving || columnNames.length === 0}
                                        clearable
                                        searchable
                                        required
                                        value={config.valueField || ""}
                                        onChange={(value) => updateConfig({ valueField: value ?? undefined })}
                                    />
                                    <Group grow>
                                        <TextInput
                                            label="Min Value"
                                            placeholder="0"
                                            type="number"
                                            disabled={isSaving}
                                            value={String(config.minValue ?? "")}
                                            onChange={(e) => updateConfig({ minValue: e.target.value ? Number(e.target.value) : undefined })}
                                        />
                                        <TextInput
                                            label="Max Value"
                                            placeholder="100"
                                            type="number"
                                            disabled={isSaving}
                                            value={String(config.maxValue ?? "")}
                                            onChange={(e) => updateConfig({ maxValue: e.target.value ? Number(e.target.value) : undefined })}
                                        />
                                        <TextInput
                                            label="Unit"
                                            placeholder="e.g. %"
                                            disabled={isSaving}
                                            value={(config.unit as string) || ""}
                                            onChange={(e) => updateConfig({ unit: e.target.value })}
                                        />
                                    </Group>
                                </>
                            )}

                            {form.values.type === "radar" && (
                                <>
                                    <Select
                                        label="Indicator Field (Axes)"
                                        data={columnNames}
                                        placeholder="Select category/name column"
                                        disabled={isSaving || columnNames.length === 0}
                                        clearable
                                        searchable
                                        required
                                        value={config.indicatorField || ""}
                                        onChange={(value) => updateConfig({ indicatorField: value ?? undefined })}
                                    />
                                    <MultiSelect
                                        label="Value Fields (Metrics)"
                                        data={columnNames}
                                        placeholder="Select numeric value columns"
                                        disabled={isSaving || columnNames.length === 0}
                                        clearable
                                        searchable
                                        required
                                        value={config.valueFields || []}
                                        onChange={(values) => updateConfig({ valueFields: values })}
                                    />
                                </>
                            )}

                            {form.values.type === "map" && (
                                <>
                                    <Select
                                        label="Region Field (Province)"
                                        data={columnNames}
                                        placeholder="Select province name column"
                                        disabled={isSaving || columnNames.length === 0}
                                        clearable
                                        searchable
                                        required
                                        value={config.regionField || ""}
                                        onChange={(value) => updateConfig({ regionField: value ?? undefined })}
                                    />
                                    <Select
                                        label="Value Field (Metric)"
                                        data={columnNames}
                                        placeholder="Select numeric value column"
                                        disabled={isSaving || columnNames.length === 0}
                                        clearable
                                        searchable
                                        required
                                        value={config.valueField || ""}
                                        onChange={(value) => updateConfig({ valueField: value ?? undefined })}
                                    />
                                    <Group grow>
                                        <TextInput
                                            label="Min Value"
                                            type="number"
                                            disabled={isSaving}
                                            value={String(config.minValue ?? "")}
                                            onChange={(e) => updateConfig({ minValue: e.target.value ? Number(e.target.value) : undefined })}
                                        />
                                        <TextInput
                                            label="Max Value"
                                            type="number"
                                            disabled={isSaving}
                                            value={String(config.maxValue ?? "")}
                                            onChange={(e) => updateConfig({ maxValue: e.target.value ? Number(e.target.value) : undefined })}
                                        />
                                    </Group>
                                </>
                            )}

                            {form.values.type === "correlation" && (
                                <>
                                    <Select
                                        label="X Field (Horizontal Category)"
                                        data={columnNames}
                                        placeholder="Select category column"
                                        disabled={isSaving || columnNames.length === 0}
                                        clearable
                                        searchable
                                        required
                                        value={config.xField || ""}
                                        onChange={(value) => updateConfig({ xField: value ?? undefined })}
                                    />
                                    <Select
                                        label="Y Field (Vertical Category)"
                                        data={columnNames}
                                        placeholder="Select category column"
                                        disabled={isSaving || columnNames.length === 0}
                                        clearable
                                        searchable
                                        required
                                        value={config.yField || ""}
                                        onChange={(value) => updateConfig({ yField: value ?? undefined })}
                                    />
                                    <Select
                                        label="Value Field (Numeric)"
                                        data={columnNames}
                                        placeholder="Select numeric value column"
                                        disabled={isSaving || columnNames.length === 0}
                                        clearable
                                        searchable
                                        required
                                        value={config.valueField || ""}
                                        onChange={(value) => updateConfig({ valueField: value ?? undefined })}
                                    />
                                    <Group grow>
                                        <TextInput
                                            label="Min Value"
                                            placeholder="Automatic"
                                            type="number"
                                            disabled={isSaving}
                                            value={String(config.minValue ?? "")}
                                            onChange={(e) => updateConfig({ minValue: e.target.value ? Number(e.target.value) : undefined })}
                                        />
                                        <TextInput
                                            label="Max Value"
                                            placeholder="Automatic"
                                            type="number"
                                            disabled={isSaving}
                                            value={String(config.maxValue ?? "")}
                                            onChange={(e) => updateConfig({ maxValue: e.target.value ? Number(e.target.value) : undefined })}
                                        />
                                    </Group>
                                </>
                            )}
                        </Stack>
                    </Tabs.Panel>

                    <Tabs.Panel value="formatting">
                        <Stack gap="md">
                            <Text size="sm" fw={500}>Color Sets</Text>
                            <Group gap="xs">
                                {colorPresets.map((preset) => (
                                    <Button
                                        key={preset.name}
                                        variant="outline"
                                        size="xs"
                                        onClick={() => updateConfig({ colors: preset.colors })}
                                    >
                                        {preset.name}
                                    </Button>
                                ))}
                                <Button
                                    variant="outline"
                                    size="xs"
                                    leftSection={<ArrowUpDown size={12} />}
                                    onClick={() => updateConfig({ colors: [...(config.colors as string[] || [])].reverse() })}
                                >
                                    Reverse color list
                                </Button>
                            </Group>

                            <Text size="sm" fw={500} mt="md">Custom Colors</Text>
                            <Stack gap="xs">
                                {(config.colors as string[] || []).map((color: string, index: number) => (
                                    <Group key={index} gap="xs">
                                        <ColorInput
                                            value={color}
                                            onChange={(val) => handleUpdateColor(index, val)}
                                            style={{ flex: 1 }}
                                        />
                                        <ActionIcon
                                            color="red"
                                            variant="subtle"
                                            onClick={() => handleRemoveColor(index)}
                                        >
                                            <Trash size={16} />
                                        </ActionIcon>
                                    </Group>
                                ))}
                                <Button
                                    variant="light"
                                    leftSection={<Plus size={16} />}
                                    onClick={handleAddColor}
                                    fullWidth
                                >
                                    Add Color
                                </Button>
                            </Stack>

                            {(form.values.type === "bar" || form.values.type === "stacked-bar-line") && (
                                <>
                                    <Text size="sm" fw={500} mt="md">Conditional Formatting</Text>
                                    <ChartConditionalFormatting
                                        value={config.conditionalFormatting as any[] || []}
                                        onChange={(val: any[]) => updateConfig({ conditionalFormatting: val })}
                                    />
                                </>
                            )}
                        </Stack>
                    </Tabs.Panel>

                    <Tabs.Panel value="permissions">
                        <RoleSelector
                            value={form.values.allowedRoles || []}
                            onChange={(roles) => form.setFieldValue("allowedRoles", roles)}
                            disabled={isSaving}
                        />
                    </Tabs.Panel>

                    <Tabs.Panel value="preview">
                        {form.values.reportId ? (
                            <Box
                                p="md"
                                style={{
                                    borderRadius: 'var(--mantine-radius-md)',
                                    border: '1px solid var(--modal-border)',
                                    backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.01)' : 'rgba(0,0,0,0.01)',
                                    minHeight: '640px'
                                }}
                            >
                                {isLoadingPreview ? (
                                    <Center h={600}>
                                        <Loader color="gray" />
                                    </Center>
                                ) : previewData.length > 0 ? (
                                    <div ref={chartContainerRef} className="w-full" style={{ minHeight: '600px' }} />
                                ) : (
                                    <Center h={600}>
                                        <Text size="sm" c="dimmed">Configure fields in the "Mapping" tab to see preview</Text>
                                    </Center>
                                ) }
                            </Box>
                        ) : (
                            <Text size="sm" c="dimmed" ta="center" py="xl">
                                Select a report in the "General" tab to enable preview.
                            </Text>
                        )}
                    </Tabs.Panel>

                    <Tabs.Panel value="example">
                        <Box py="sm">
                            {activeTab === 'example' && <ChartExampleData chartType={form.values.type || 'bar'} />}
                        </Box>
                    </Tabs.Panel>
                </ConfigTabs>
            </Box>
        </BaseContainer>
    );
}
